import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  FaTwitter, FaTelegram, FaTiktok, FaInstagram, FaFacebookF,
  FaNewspaper, FaRss, FaSearch, FaTimes, FaChevronDown, FaChevronUp, FaTv,
} from 'react-icons/fa';
import { useTheme } from '../theme/ThemeContext';
import { MessageSource } from '../services/ListsManager';

const ALL_SOURCES = [
  { key: MessageSource.TWITTER, label: 'Twitter', icon: FaTwitter, color: '#1da1f2' },
  { key: MessageSource.TELEGRAM, label: 'Telegram', icon: FaTelegram, color: '#0088cc' },
  { key: MessageSource.TIKTOK, label: 'TikTok', icon: FaTiktok, color: '#ff0050' },
  { key: MessageSource.INSTAGRAM, label: 'Instagram', icon: FaInstagram, color: '#e1306c' },
  { key: MessageSource.FACEBOOK_GROUP, label: 'Facebook', icon: FaFacebookF, color: '#1877F2' },
  { key: 'rss', label: 'RSS', icon: FaRss, color: '#f97316' },
  { key: MessageSource.WEBZ, label: 'News', icon: FaNewspaper, color: '#6366f1' },
  { key: MessageSource.TV, label: 'TV', icon: FaTv, color: '#9f1239' },
];

const DATE_PRESETS = [
  { label: 'Today', days: 1 },
  { label: '7d', days: 7 },
  { label: '14d', days: 14 },
  { label: '30d', days: 30 },
];

const PLATFORM_META = [
  { key: 'twitter', label: 'Twitter', icon: FaTwitter, color: '#1da1f2' },
  { key: 'telegram', label: 'Telegram', icon: FaTelegram, color: '#0088cc' },
  { key: 'tiktok', label: 'TikTok', icon: FaTiktok, color: '#ff0050' },
  { key: 'instagram', label: 'Instagram', icon: FaInstagram, color: '#e1306c' },
  { key: 'facebook_group', label: 'Facebook', icon: FaFacebookF, color: '#1877F2' },
  { key: 'rss', label: 'RSS', icon: FaRss, color: '#f97316' },
  { key: 'webz', label: 'News', icon: FaNewspaper, color: '#6366f1' },
  { key: 'tv', label: 'TV', icon: FaTv, color: '#9f1239' },
];

export default function FilterDropdown({
  isOpen, onClose, triggerRef, tweets,
  sources, fromDate, toDate, selectedUsers,
  onApply,
}) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const dropdownRef = useRef(null);

  const [draftSources, setDraftSources] = useState(sources || ALL_SOURCES.map(s => s.key));
  const [draftPreset, setDraftPreset] = useState(null);
  const [draftFromDate, setDraftFromDate] = useState(fromDate || '');
  const [draftToDate, setDraftToDate] = useState(toDate || '');
  const [showCustomDate, setShowCustomDate] = useState(false);

  const [accountSearch, setAccountSearch] = useState('');
  const [draftSelectedUsers, setDraftSelectedUsers] = useState(selectedUsers || []);

  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (isOpen) {
      setDraftSources(sources || ALL_SOURCES.map(s => s.key));
      setDraftFromDate(fromDate || '');
      setDraftToDate(toDate || '');
      setDraftSelectedUsers(selectedUsers || []);
      setDraftPreset(null);
      setShowCustomDate(!!(fromDate || toDate));
    }
  }, [isOpen]); // eslint-disable-line

  useEffect(() => {
    if (!isOpen) return;
    const rect = triggerRef?.current?.getBoundingClientRect();
    if (rect) {
      setPos({ top: rect.bottom + 6, left: rect.left });
    }
  }, [isOpen, triggerRef]);

  const platformAccounts = useMemo(() => {
    if (!tweets || tweets.length === 0) return {};
    const grouped = {};
    tweets.forEach(tweet => {
      const username = tweet.username || tweet.author_name;
      if (!username) return;
      const platform = (tweet.source || 'twitter').toLowerCase();
      if (!grouped[platform]) grouped[platform] = new Set();
      grouped[platform].add(username);
    });
    const result = {};
    Object.entries(grouped).forEach(([platform, set]) => {
      result[platform] = [...set].sort((a, b) => a.localeCompare(b));
    });
    return result;
  }, [tweets]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (triggerRef?.current?.contains(e.target)) return;
      if (dropdownRef.current?.contains(e.target)) return;
      onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, onClose, triggerRef]);

  if (!isOpen) return null;

  const T = {
    bg: theme.bgCard,
    surface: theme.bgSurface,
    border: theme.border,
    text: theme.textPrimary,
    textSec: theme.textSecondary,
    textMuted: theme.textMuted,
    primary: '#4DA6FF',
  };

  const toggleSource = (key) => {
    setDraftSources(prev =>
      prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key]
    );
  };

  const selectPreset = (days) => {
    if (draftPreset === days) {
      setDraftPreset(null);
      setDraftFromDate('');
      setDraftToDate('');
      return;
    }
    setDraftPreset(days);
    const now = new Date();
    const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    setDraftFromDate(from.toISOString());
    setDraftToDate(now.toISOString());
    setShowCustomDate(false);
  };

  const toggleUser = (username) => {
    setDraftSelectedUsers(prev =>
      prev.includes(username) ? prev.filter(u => u !== username) : [...prev, username]
    );
  };

  const handleReset = () => {
    setDraftSources(ALL_SOURCES.map(s => s.key));
    setDraftFromDate('');
    setDraftToDate('');
    setDraftSelectedUsers([]);
    setDraftPreset(null);
    setShowCustomDate(false);
  };

  const handleApply = () => {
    onApply({
      sources: draftSources,
      fromDate: draftFromDate || null,
      toDate: draftToDate || null,
      selectedUsers: draftSelectedUsers.length > 0 ? draftSelectedUsers : null,
    });
    onClose();
  };

  const activeFilterCount = (() => {
    let count = 0;
    if (draftSources.length < ALL_SOURCES.length) count++;
    if (draftFromDate) count++;
    if (draftSelectedUsers.length > 0) count++;
    return count;
  })();

  const hasAnyAccounts = platformAccounts && Object.keys(platformAccounts).length > 0;

  return ReactDOM.createPortal(
    <div ref={dropdownRef} style={{
      position: 'fixed',
      top: pos.top,
      left: Math.min(pos.left, window.innerWidth - 380),
      zIndex: 9999,
      width: 360,
      maxHeight: 540,
      overflowY: 'auto',
      borderRadius: 14,
      border: `1px solid ${T.border}`,
      backgroundColor: T.bg,
      boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
      padding: '14px 16px',
      display: 'flex',
      flexDirection: 'column',
      gap: 14,
    }}>
      {/* Sources */}
      <div>
        <div style={{ color: T.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, marginBottom: 8 }}>
          {t('app.lists.sources')}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {ALL_SOURCES.map(src => {
            const Icon = src.icon;
            const isActive = draftSources.includes(src.key);
            return (
              <div
                key={src.key}
                onClick={() => toggleSource(src.key)}
                title={src.label}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '6px 8px', borderRadius: 20, cursor: 'pointer',
                  background: isActive ? `${src.color}20` : T.surface,
                  border: `1px solid ${isActive ? src.color : T.border}`,
                  opacity: isActive ? 1 : 0.5,
                }}
              >
                <Icon size={13} color={src.color} />
              </div>
            );
          })}
        </div>
      </div>

      {/* Date Range */}
      <div>
        <div style={{ color: T.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, marginBottom: 8 }}>
          {t('app.lists.dateRange')}
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
          {DATE_PRESETS.map(p => (
            <div
              key={p.days}
              onClick={() => selectPreset(p.days)}
              style={{
                padding: '5px 10px', borderRadius: 20, cursor: 'pointer',
                background: draftPreset === p.days ? `${T.primary}20` : T.surface,
                border: `1px solid ${draftPreset === p.days ? T.primary : T.border}`,
                color: draftPreset === p.days ? T.primary : T.textMuted,
                fontSize: 11, fontWeight: 600,
              }}
            >
              {p.label}
            </div>
          ))}
          <div
            onClick={() => { setShowCustomDate(!showCustomDate); setDraftPreset(null); }}
            style={{
              padding: '5px 8px', borderRadius: 20, cursor: 'pointer',
              background: showCustomDate && !draftPreset ? `${T.primary}20` : T.surface,
              border: `1px solid ${showCustomDate && !draftPreset ? T.primary : T.border}`,
              color: T.textMuted, fontSize: 11, display: 'flex', alignItems: 'center',
            }}
          >
            {showCustomDate ? <FaChevronUp size={8} /> : <FaChevronDown size={8} />}
          </div>
        </div>
        {showCustomDate && (
          <div style={{
            display: 'flex', gap: 8, alignItems: 'center',
            padding: '10px 12px', borderRadius: 10,
            background: T.surface, border: `1px solid ${T.border}`,
          }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', color: T.primary, fontSize: 9, fontWeight: 700, letterSpacing: 0.5, marginBottom: 4 }}>
                {t('app.lists.fromDate')}
              </label>
              <input
                type="date"
                value={draftFromDate ? draftFromDate.slice(0, 10) : ''}
                onChange={(e) => { setDraftFromDate(e.target.value ? `${e.target.value}T00:00:00.000Z` : ''); setDraftPreset(null); }}
                style={{
                  width: '100%', padding: '5px 6px', borderRadius: 6,
                  border: `1px solid ${T.border}`, background: T.bg,
                  color: T.text, fontSize: 12, outline: 'none',
                  colorScheme: theme.bg === '#050505' ? 'dark' : 'light',
                }}
              />
            </div>
            <span style={{ color: T.textMuted, fontSize: 13, marginTop: 14 }}>→</span>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', color: T.primary, fontSize: 9, fontWeight: 700, letterSpacing: 0.5, marginBottom: 4 }}>
                {t('app.lists.toDate')}
              </label>
              <input
                type="date"
                value={draftToDate ? draftToDate.slice(0, 10) : ''}
                onChange={(e) => { setDraftToDate(e.target.value ? `${e.target.value}T23:59:59.999Z` : ''); setDraftPreset(null); }}
                style={{
                  width: '100%', padding: '5px 6px', borderRadius: 6,
                  border: `1px solid ${T.border}`, background: T.bg,
                  color: T.text, fontSize: 12, outline: 'none',
                  colorScheme: theme.bg === '#050505' ? 'dark' : 'light',
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Accounts — grouped by platform */}
      <div>
        <div style={{ color: T.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, marginBottom: 8 }}>
          {t('app.lists.accounts')}
          {draftSelectedUsers.length > 0 && (
            <span style={{ color: T.primary, marginLeft: 6 }}>({draftSelectedUsers.length})</span>
          )}
        </div>
        {!hasAnyAccounts ? (
          <div style={{ color: T.textMuted, fontSize: 11 }}>{t('app.lists.noAccounts')}</div>
        ) : (
          <>
            {/* Search */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '5px 8px', borderRadius: 8,
              background: T.surface, border: `1px solid ${T.border}`,
              marginBottom: 8,
            }}>
              <FaSearch size={9} color={T.textMuted} />
              <input
                type="text"
                value={accountSearch}
                onChange={(e) => setAccountSearch(e.target.value)}
                placeholder={t('app.lists.searchAccounts')}
                style={{
                  border: 'none', outline: 'none', background: 'transparent',
                  color: T.text, fontSize: 11, flex: 1,
                }}
              />
              {accountSearch && (
                <FaTimes size={8} color={T.textMuted} style={{ cursor: 'pointer' }}
                  onClick={() => setAccountSearch('')} />
              )}
            </div>

            {/* Platform sections */}
            <div style={{ maxHeight: 150, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {PLATFORM_META.map(section => {
                const accounts = platformAccounts[section.key];
                if (!accounts || accounts.length === 0) return null;
                const filtered = accounts.filter(u => !accountSearch || u.toLowerCase().includes(accountSearch.toLowerCase()));
                if (filtered.length === 0) return null;
                const Icon = section.icon;
                return (
                  <div key={section.key}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
                      <Icon size={9} color={section.color} />
                      <span style={{ color: section.color, fontSize: 10, fontWeight: 700 }}>{section.label}</span>
                      <span style={{ color: T.textMuted, fontSize: 9 }}>({filtered.length})</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 1, paddingLeft: 4 }}>
                      {filtered.slice(0, 30).map(username => {
                        const isSelected = draftSelectedUsers.includes(username);
                        return (
                          <div
                            key={`${section.key}-${username}`}
                            onClick={() => toggleUser(username)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 8,
                              padding: '4px 6px', borderRadius: 5, cursor: 'pointer',
                              background: isSelected ? `${T.primary}12` : 'transparent',
                            }}
                          >
                            <div style={{
                              width: 13, height: 13, borderRadius: 3,
                              border: `1.5px solid ${isSelected ? T.primary : T.border}`,
                              background: isSelected ? T.primary : 'transparent',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              flexShrink: 0,
                            }}>
                              {isSelected && <span style={{ color: '#fff', fontSize: 8, fontWeight: 700 }}>✓</span>}
                            </div>
                            <span style={{ color: T.text, fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              @{username}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 6, borderTop: `1px solid ${T.border}` }}>
        <button
          onClick={handleReset}
          style={{
            padding: '6px 14px', borderRadius: 8,
            border: `1px solid ${T.border}`, background: 'transparent',
            color: T.textSec, fontSize: 11, fontWeight: 600, cursor: 'pointer',
          }}
        >
          {t('app.lists.reset')}
        </button>
        <button
          onClick={handleApply}
          style={{
            padding: '6px 14px', borderRadius: 8,
            border: 'none', background: T.primary,
            color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer',
          }}
        >
          {t('app.lists.apply')}
          {activeFilterCount > 0 && ` (${activeFilterCount})`}
        </button>
      </div>
    </div>,
    document.body
  );
}
